import { combineReducers } from 'redux';

import { loadOrgImpersonationId } from 'services/stateStorage/index';
import {
  LOG_IN_REQUESTED,
  LOG_OUT_REQUESTED,
  GET_FEATURE_FLAGS_REQUESTED,
  GET_FEATURE_FLAGS_SUCCESS,
  UPDATE_FEATURE_FLAGS_SUCCESS,
  SET_SESSION_SUCCESS,
  SET_SESSION_FAILURE,
  CREATE_ORG_SUCCESS,
  IMPERSONATE_ORG,
  STOP_ORG_IMPERSONATION,
  SEARCH_ORGS_REQUESTED,
  SEARCH_ORGS_SUCCESS,
  SEARCH_ORGS_FAILURE,
  SORT_ORGS,
  START_MFA_SIGNUP_FLOW_REQUESTED,
  START_MFA_SIGNUP_FLOW_SUCCESS,
  START_MFA_SIGNUP_FLOW_FAILURE,
  GET_MFA_STATUS_REQUESTED,
  GET_MFA_STATUS_SUCCESS,
  GET_MFA_STATUS_FAILURE,
  REMOVE_MFA_ENROLLMENT_REQUESTED,
  REMOVE_MFA_ENROLLMENT_SUCCESS,
  REMOVE_MFA_ENROLLMENT_FAILURE,
  AWS_SIGNUP_FAILURE,
} from '../actions';

/**
 * Because of the way state is set to initial state by default in these reducers,
 * impersonatedOrganizationId needs to check local storage first because LOG_IN_REQUESTED
 * is called when the page refreshes and would normally set this variable back to null,
 * causing a user to stop impersonating an org when they have not requested that action
 */
export const initialState = {
  credentials: {
    accessToken: null,
  },
  session: {
    originatingURL: null,
    error: null,
  },
  featureFlags: {
    isLoading: false,
    flags: {},
  },
  identity: {
    id: null,
    ORv4ID: null,
    name: null,
    email: null,
    emailVerified: false,
    organizationId: null,
    roles: null,
    permissions: null,
    impersonatedOrganizationId: loadOrgImpersonationId(localStorage),
    orgSearchList: {
      query: null,
      sort: null,
      list: [],
    },
    MFAEnabled: false,
  },
};

const featureFlagsReducer = (state = initialState.featureFlags, action) => {
  switch (action.type) {
    case GET_FEATURE_FLAGS_REQUESTED:
      return {
        ...state,
        flags: {},
        isLoading: true,
      };

    case GET_FEATURE_FLAGS_SUCCESS:
      return {
        ...state,
        flags: action.payload,
        isLoading: false,
      };

    case UPDATE_FEATURE_FLAGS_SUCCESS:
      return {
        ...state,
        flags: { ...state.flags, ...action.payload },
      };

    case LOG_IN_REQUESTED:
    case LOG_OUT_REQUESTED:
    case SET_SESSION_FAILURE:
      return {
        ...state,
        flags: {},
        isLoading: false,
      };

    default:
      return state;
  }
};

const credentialsReducer = (state = initialState.credentials, action) => {
  switch (action.type) {
    case SET_SESSION_SUCCESS:
      return {
        ...state,
        accessToken: action.payload.accessToken,
      };

    case LOG_IN_REQUESTED:
    case LOG_OUT_REQUESTED:
      return {
        ...state,
        accessToken: null,
      };

    default:
      return state;
  }
};

const sessionReducer = (state = initialState.session, action) => {
  switch (action.type) {
    case SET_SESSION_FAILURE:
      return {
        ...state,
        error: action.error,
        originatingURL: null,
      };

    case AWS_SIGNUP_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    case LOG_IN_REQUESTED:
      return {
        ...state,
        originatingURL: action.payload,
        error: null,
      };

    case SET_SESSION_SUCCESS:
    case LOG_OUT_REQUESTED:
      return {
        ...state,
        originatingURL: null,
        error: null,
      };

    default:
      return state;
  }
};

const identityReducer = (state = initialState.identity, action) => {
  switch (action.type) {
    case SET_SESSION_SUCCESS: {
      const namespacedData = action.payload.idTokenPayload[process.env.REACT_APP_AUTH0_AUDIENCE];
      /**
       * The emailVerified field value is set with an || operator to avoid an unidentified bug
       * with the same field in the idTokenPayload being mutated during API calls to Auth0
       */
      return {
        ...state,
        id: namespacedData.userId,
        ORv4ID: namespacedData.ORv4ID,
        name: namespacedData.name,
        email: namespacedData.email,
        emailVerified: namespacedData.email_verified || namespacedData.emailVerified,
        organizationId: namespacedData.organizationId,
        roles: namespacedData.roles,
        permissions: namespacedData.permissions,
      };
    }

    case LOG_IN_REQUESTED:
      /**
       * impersonatedOrganizationId still needs to check if it has a value
       * from the state being passed in before reverting to null
       */
      return {
        ...state,
        id: null,
        ORv4ID: null,
        name: null,
        email: null,
        emailVerified: false,
        organizationId: null,
        roles: null,
        permissions: null,
        impersonatedOrganizationId: state.impersonatedOrganizationId || null,
      };

    case LOG_OUT_REQUESTED:
      return {
        ...state,
        id: null,
        ORv4ID: null,
        name: null,
        email: null,
        emailVerified: false,
        organizationId: null,
        roles: null,
        permissions: null,
        impersonatedOrganizationId: null,
      };

    case CREATE_ORG_SUCCESS:
      return {
        ...state,
        organizationId: action.payload.id,
      };

    case IMPERSONATE_ORG:
      return {
        ...state,
        impersonatedOrganizationId: action.payload,
      };

    case STOP_ORG_IMPERSONATION:
      return {
        ...state,
        impersonatedOrganizationId: null,
        orgSearchList: {
          query: null,
          sort: null,
          list: [],
        },
      };

    case SEARCH_ORGS_REQUESTED:
      return {
        ...state,
        orgSearchList: {
          ...state.orgSearchList,
          query: action.payload,
        },
      };

    case SEARCH_ORGS_SUCCESS:
      return {
        ...state,
        orgSearchList: {
          ...state.orgSearchList,
          list: action.payload,
        },
      };

    case SEARCH_ORGS_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    case SORT_ORGS:
      return {
        ...state,
        orgSearchList: {
          ...state.orgSearchList,
          list: action.payload.list || state.orgSearchList.list,
          sort: action.payload.sortBy,
        },
      };

    case START_MFA_SIGNUP_FLOW_REQUESTED:
    case START_MFA_SIGNUP_FLOW_SUCCESS:
      return {
        ...state,
      };

    case START_MFA_SIGNUP_FLOW_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    case GET_MFA_STATUS_REQUESTED:
      return {
        ...state,
      };

    case GET_MFA_STATUS_SUCCESS:
      return {
        ...state,
        MFAEnabled: action.payload.length > 0 && action.payload[0].status === 'confirmed',
        enrollmentId: (action.payload.length > 0 && action.payload[0].id) || '',
      };

    case GET_MFA_STATUS_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    case REMOVE_MFA_ENROLLMENT_REQUESTED:
      return {
        ...state,
      };

    case REMOVE_MFA_ENROLLMENT_SUCCESS:
      return {
        ...state,
        MFAEnabled: false,
      };

    case REMOVE_MFA_ENROLLMENT_FAILURE:
      return {
        ...state,
        error: action.payload,
      };

    default:
      return state;
  }
};

export default combineReducers({
  session: sessionReducer,
  credentials: credentialsReducer,
  featureFlags: featureFlagsReducer,
  identity: identityReducer,
});
